import { useState } from "react";
import { FiX } from "react-icons/fi";
import {
  DEFAULT_ARTIFACT_SCOPE,
  ARTIFACT_SCOPES,
  ARTIFACT_SCOPE_LABELS,
} from "@kagami/shared/artifactScope";
import {
  DEFAULT_ARTIFACT_VISIBILITY,
  ArtifactVisibility,
} from "@kagami/shared/artifactVisibility";
import Button from "../ui/Button";
import { Input } from "../ui/shadcn/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/shadcn/select";
import { Switch } from "../ui/shadcn/switch";

export default function ArtifactEditorModal({ onClose, prompt, title }) {
  const [name, setName] = useState(prompt?.name || "");
  const [scope, setScope] = useState(prompt?.scope || DEFAULT_ARTIFACT_SCOPE);
  const [visibility, setVisibility] = useState(
    prompt?.visibility || DEFAULT_ARTIFACT_VISIBILITY,
  );

  const visibilityLabel =
    visibility === ArtifactVisibility.Public
      ? "Public — Visible to everyone"
      : "Private — Visible to you only";

  const handleSave = () => {
    if (!name.trim()) return;
    onClose({ name: name.trim(), scope, visibility });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
  };

  return (
    <div
      id="ArtifactEditorModal"
      className="w-full max-w-[420px] rounded-md flex flex-col bg-background border">
      <div className="px-4 pt-4 flex justify-between items-center">
        <h1 className="text-xl font-bold border-l-4 border-primary pl-4 capitalize">
          Save Artifact
        </h1>
        <button
          type="button"
          onClick={() => onClose(null)}
          className="shrink-0 -mr-1 -mt-1 p-1.5 rounded-md text-secondary-foreground/70 hover:text-foreground hover:bg-secondary/60 cursor-pointer"
          title="Close">
          <FiX className="w-5 h-5" />
        </button>
      </div>
      <div className="flex flex-col gap-4 px-4 py-8">
        <div className="flex gap-2">
          <div className="flex flex-col gap-1 flex-1">
            <label className="text-xs text-secondary-foreground/80">Name</label>
            <Input
              id="ArtifactEditorModal__name"
              type="text"
              placeholder="Artifact name"
              value={name}
              className="text-xs placeholder:text-secondary-foreground/50"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-secondary-foreground/80">
              Scope
            </label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger id="ArtifactEditorModal__scope">
                <SelectValue placeholder="Select scope" />
              </SelectTrigger>
              <SelectContent className="bg-popover z-[100]">
                {ARTIFACT_SCOPES.map((option) => (
                  <SelectItem
                    id={`ArtifactEditorModal__scopeOption-${option}`}
                    key={option}
                    value={option}
                    className="text-xs">
                    {ARTIFACT_SCOPE_LABELS[option]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* This dialog only ever creates a brand-new artifact under the
              current user (a fresh save, or a "Save As" copy of someone
              else's) — never edits an existing one in place — so visibility
              is always the current user's own to set, regardless of who
              owned whatever the copy started from. */}
          <Switch
            id="prompt-visibility"
            checked={visibility === ArtifactVisibility.Public}
            onCheckedChange={(checked) =>
              setVisibility(
                checked
                  ? ArtifactVisibility.Public
                  : ArtifactVisibility.Private,
              )
            }
          />
          <label
            htmlFor="prompt-visibility"
            className="text-xs text-secondary-foreground">
            {visibilityLabel}
          </label>
        </div>
      </div>
      <div className="flex justify-between p-2">
        <Button
          id="ArtifactEditorModal__cancel"
          type="danger-ghost"
          className="min-w-20"
          onClick={() => onClose(null)}>
          Cancel
        </Button>
        <Button
          id="ArtifactEditorModal__save"
          type="primary"
          className="min-w-20"
          onClick={handleSave}
          disabled={!name.trim()}>
          Save
        </Button>
      </div>
    </div>
  );
}
